import * as z from "zod";
import { nanoid } from "nanoid";
import { zql } from "../generatedDrizzleSchema";
import { mutator } from "./mutator";

const updateUsageBudgetSchema = z.object({
  id: z.string().optional(),
  spaceId: z.string().optional(),
  connectorId: z.string().optional(),
  measure: z.enum(["requests", "tokens", "spend_usd"]),
  scope: z.enum(["total", "personal"]).optional(),
  limitAmount: z.number().positive(),
  warnThresholdPct: z.number().min(1).max(100).optional(),
});

export const updateUsageBudget = mutator({
  schema: updateUsageBudgetSchema,
  resolve: {},
  permissions: [
    {
      permission: "tenant:ai_providers_manage",
      scopeId: ({ ctx }) => ctx.tenantId,
    },
  ],
  fn: async ({ tx, ctx, args }) => {
    const now = Date.now();
    const connectorId = args.connectorId ?? null;
    const scope = args.scope ?? "total";
    // A personal budget is global to every member's personal space, never
    // space-scoped, so it must not carry a spaceId — otherwise it would both
    // apply to all personal spaces and match the per-space lookup for that id.
    const spaceId = scope === "personal" ? null : (args.spaceId ?? null);

    // Resolve by the exact dimension slice, never a caller-supplied id which
    // could name a budget in another scope; the (dimensions, measure, scope)
    // tuple is the natural key enforced by the NULLS NOT DISTINCT unique index.
    // scope must be part of the lookup or the personal budget (space_id null,
    // like total) would resolve to and overwrite the total row. The tenantId
    // filter keeps the unscoped lookup (no RLS) from updating another tenant's
    // budget row for a foreign spaceId/connectorId.
    let scoped = zql.connectorUsageBudgets
      .where("tenantId", ctx.tenantId)
      .where("measure", args.measure)
      .where("scope", scope);
    scoped =
      spaceId === null
        ? scoped.where("spaceId", "IS", null)
        : scoped.where("spaceId", spaceId);
    scoped =
      connectorId === null
        ? scoped.where("connectorId", "IS", null)
        : scoped.where("connectorId", connectorId);
    const existing = await tx.run(scoped.one());

    const values = {
      spaceId,
      connectorId,
      measure: args.measure,
      scope,
      limitAmount: args.limitAmount,
      warnThresholdPct:
        args.warnThresholdPct ?? existing?.warnThresholdPct ?? 80,
      updatedAt: now,
    };
    if (existing) {
      await tx.mutate.connectorUsageBudgets.update({
        id: existing.id,
        ...values,
      });
      return;
    }
    await tx.mutate.connectorUsageBudgets.insert({
      id: args.id ?? nanoid(12),
      ...values,
      createdAt: now,
      tenantId: ctx.tenantId,
    });
  },
});
